import type { LeadStageKey } from '@crm/shared';
import { cn, Modal } from '@crm/ui';
import { Info } from 'lucide-react';
import { useState } from 'react';
import type { FunnelRow } from '../api';

const porcentagem = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

const FAIXA = 56;
const LARGURA = 320;
/** Respiro entre as faixas - sem ele o funil vira um bloco so. */
const FOLGA = 4;

/**
 * O funil e simetrico e afunila sempre igual: a largura desenha a etapa, nao a
 * quantidade. Barra proporcional ao numero deixava o desenho torto quando uma
 * etapa tinha muito mais gente que a anterior - a quantidade fica escrita
 * dentro da faixa, que e onde se le numero.
 */
const LARGURAS = [1, 0.82, 0.64, 0.46, 0.28, 0.18];

/** Uma cor por etapa, esquentando ate o verde de quem agendou. */
const CORES: Record<string, { de: string; para: string; ponto: string }> = {
  MESSAGE_SENT: { de: '#fbbf24', para: '#f59e0b', ponto: 'bg-amber-500' },
  LEAD: { de: '#38bdf8', para: '#0ea5e9', ponto: 'bg-sky-500' },
  AUTO_CONTACT: { de: '#c084fc', para: '#a855f7', ponto: 'bg-purple-500' },
  HUMAN_CONTACT: { de: '#818cf8', para: '#6366f1', ponto: 'bg-indigo-500' },
  SCHEDULED: { de: '#34d399', para: '#10b981', ponto: 'bg-emerald-500' },
};

const INICIOS = [
  { key: 'MESSAGE_SENT', label: 'Da mensagem automatica' },
  { key: 'LEAD', label: 'Do lead' },
] as const;

/**
 * O funil de conversao. Cada numero e a quantidade de *leads* que passou por
 * aquela etapa - quem trocou dez mensagens continua sendo um lead.
 *
 * O percentual compara com a etapa em que o funil comeca, nao com a de cima:
 * atendimento do assistente e atendimento humano acontecem em paralelo, e
 * encadear os dois daria um numero sem sentido.
 */
export function FunnelChart({
  rows,
  contacts,
  inicio,
  onInicioChange,
  onSelectStage,
}: {
  rows: FunnelRow[];
  /**
   * Leads que falaram com alguem - bot ou pessoa - sem repetir quem passou
   * pelos dois. Vem da API porque somar as duas etapas contaria em dobro.
   */
  contacts?: number;
  inicio: LeadStageKey;
  /**
   * Sem isto o seletor "A partir de" nao aparece - no painel quem manda na
   * coorte e o filtro de gatilho, la em cima, e dois controles para a mesma
   * coisa se contradiriam.
   */
  onInicioChange?: (stage: LeadStageKey) => void;
  onSelectStage?: (stage: string) => void;
}) {
  const [explicando, setExplicando] = useState<{ label: string; dica: string } | null>(null);
  const porEtapa = new Map(rows.map((row) => [row.stage, row.total]));

  // Os degraus do desenho nao sao as cinco etapas do quadro: contato do bot e
  // contato de gente acontecem em paralelo e viram uma linha so, "Contatos".
  // O numero dela vem pronto da API - somar as duas repetiria quem passou
  // pelas duas. Sem esse numero, sobra o maior dos dois, que ao menos nunca
  // conta ninguem duas vezes.
  const contatos =
    contacts ?? Math.max(porEtapa.get('AUTO_CONTACT') ?? 0, porEtapa.get('HUMAN_CONTACT') ?? 0);

  const DEGRAUS = [
    {
      key: 'MESSAGE_SENT' as const,
      label: 'Mensagens disparadas',
      dica: 'Pessoas que receberam um disparo de campanha ou um aviso automatico do sistema. E a boca do funil de quem veio por marketing - quem chega sozinho nao passa por aqui.',
    },
    {
      key: 'LEAD' as const,
      label: 'Leads',
      dica: 'Quem escreveu e ainda nao foi atendido. Um lead e uma pessoa, nao uma mensagem: quem trocou dez mensagens continua sendo um.',
    },
    {
      key: 'AUTO_CONTACT' as const,
      label: 'Contatos',
      dica: 'Leads que falaram com alguem - o assistente ou uma pessoa do salao. Quem passou pelos dois conta uma vez so.',
    },
    {
      key: 'SCHEDULED' as const,
      label: 'Agendamentos',
      dica: 'Leads que viraram horario na agenda. E o degrau que fecha o funil: dali em diante o assunto e comparecer.',
    },
  ];

  // Lead organico nunca recebeu disparo: sem esse degrau, o funil dele
  // comecaria numa faixa vazia.
  const etapas = DEGRAUS.filter(
    (degrau) => degrau.key !== 'MESSAGE_SENT' || inicio === 'MESSAGE_SENT',
  ).map((degrau) => ({
    key: degrau.key,
    label: degrau.label,
    dica: degrau.dica,
    total: degrau.key === 'AUTO_CONTACT' ? contatos : (porEtapa.get(degrau.key) ?? 0),
  }));

  const boca = etapas[0]?.total ?? 0;
  const largura = (index: number) => (LARGURAS[index] ?? 0.16) * LARGURA;
  const meio = LARGURA / 2;

  return (
    <div className="space-y-3">
      {onInicioChange ? (
        <div className="flex flex-wrap items-center gap-1">
          <span className="mr-1 text-xs text-muted-foreground">A partir de</span>
          {INICIOS.map((opcao) => (
            <button
              key={opcao.key}
              type="button"
              onClick={() => onInicioChange(opcao.key)}
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                inicio === opcao.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground',
              )}
            >
              {opcao.label}
            </button>
          ))}
        </div>
      ) : null}

      <ol className="max-w-3xl">
        {etapas.map((etapa, index) => {
          const conversao = boca > 0 ? (etapa.total / boca) * 100 : 0;
          const atual = largura(index);
          const proxima = largura(index + 1);
          const cor = CORES[etapa.key] ?? CORES.LEAD;
          const topo = FOLGA / 2;
          const fundo = FAIXA - FOLGA / 2;

          return (
            <li key={etapa.key} className="flex items-center gap-2 sm:gap-4">
              <span className="flex w-28 shrink-0 items-center gap-2 sm:w-48">
                <span className={cn('h-2 w-2 shrink-0 rounded-full', cor?.ponto)} aria-hidden />
                <span className="text-xs leading-tight sm:text-sm">{etapa.label}</span>
              </span>

              <button
                type="button"
                disabled={!onSelectStage}
                onClick={() => onSelectStage?.(etapa.key)}
                className={cn(
                  'relative min-w-0 flex-1 transition-opacity',
                  onSelectStage && 'cursor-pointer hover:opacity-90',
                )}
              >
                <svg
                  viewBox={`0 0 ${LARGURA} ${FAIXA}`}
                  preserveAspectRatio="none"
                  className="h-11 w-full"
                  role="img"
                  aria-label={`${etapa.label}: ${etapa.total} leads`}
                >
                  <title>{`${etapa.label}: ${etapa.total} leads`}</title>
                  <defs>
                    <linearGradient id={`funil-${etapa.key}`} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={cor?.de} />
                      <stop offset="100%" stopColor={cor?.para} />
                    </linearGradient>
                  </defs>
                  <polygon
                    points={`${meio - atual / 2},${topo} ${meio + atual / 2},${topo} ${meio + proxima / 2},${fundo} ${meio - proxima / 2},${fundo}`}
                    fill={`url(#funil-${etapa.key})`}
                  />
                </svg>

                {/* O numero fora do SVG: com `preserveAspectRatio=none` a faixa
                    estica com a tela, e o texto esticava junto - em tela larga
                    virava um risco. */}
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold text-white">
                  {etapa.total}
                </span>
              </button>

              <span className="flex shrink-0 items-center gap-3">
                <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">
                  {index === 0 ? '—' : `${porcentagem.format(conversao)}%`}
                </span>

                <button
                  type="button"
                  aria-label={`O que e ${etapa.label}`}
                  onClick={() => setExplicando(etapa)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </span>
            </li>
          );
        })}
      </ol>

      <Modal
        open={Boolean(explicando)}
        onClose={() => setExplicando(null)}
        title={explicando?.label ?? ''}
        size="small"
        actions={[{ label: 'Entendi', onClick: () => setExplicando(null) }]}
      >
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>{explicando?.dica}</p>
          <p>
            O percentual compara com o degrau em que o funil comeca, nao com o de cima: contato do
            assistente e contato humano acontecem em paralelo, e encadear os dois daria um numero
            sem sentido.
          </p>
        </div>
      </Modal>
    </div>
  );
}
